/**
 * Titan Electric — Employees API helper
 *
 * Talks to the SQLite-backed /api/users endpoints. Replaces the old
 * localStorage-based services/employeeData.js for anything admin-facing.
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try {
    const raw = localStorage.getItem('titan_session');
    return raw ? JSON.parse(raw).token : null;
  } catch {
    return null;
  }
}

async function apiFetch(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers['x-worker-token'] = token;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// Returns active employees with pin (plaintext, admin view), trelloId, etc.
export async function fetchEmployees() {
  return apiFetch('/users');
}

export async function createEmployee({ firstName, lastName, email, phone, role, pin, isCdl, isShop, trade }) {
  return apiFetch('/users', {
    method: 'POST',
    body: JSON.stringify({ firstName, lastName, email, phone, role, pin: pin || undefined, isCdl: isCdl ? 1 : 0, isShop: isShop ? 1 : 0, trade: trade || null }),
  });
}

export async function updateEmployee(id, { firstName, lastName, email, phone, role, status, isCdl, isShop, trade, address, city, state, zip }) {
  return apiFetch(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ firstName, lastName, email, phone, role, status, isCdl: isCdl ? 1 : 0, isShop: isShop ? 1 : 0, trade: trade || null,
      address: address || null, city: city || null, state: state || null, zip: zip || null }),   // home address (Nov 2 2026)
  });
}

// Soft delete — sets status to 'inactive' server-side.
export async function deactivateEmployee(id) {
  return apiFetch(`/users/${id}`, { method: 'DELETE' });
}

export async function setEmployeePin(id, pin) {
  return apiFetch(`/users/${id}/pin`, {
    method: 'PATCH',
    body: JSON.stringify({ pin }),
  });
}

export async function setStaffCredentials(id, { username, password }) {
  return apiFetch(`/users/${id}/credentials`, {
    method: 'PATCH',
    body: JSON.stringify({ username, password }),
  });
}

// A person's own mailbox so the app can email as them. The password goes up once and is
// stored encrypted; nothing ever reads it back. Blank both to clear.
export async function setMailCredentials(id, { mailUser, mailPass }) {
  return apiFetch(`/users/${id}/mail`, { method: 'PATCH', body: JSON.stringify({ mail_user: mailUser || '', mail_pass: mailPass || '' }) });
}
export async function testMailCredentials(id) {
  return apiFetch(`/users/${id}/mail/test`, { method: 'POST' });
}

export const ROLES = ['Worker', 'ShopWorker', 'FieldSupervisor', 'Accounting', 'ProjectManager', 'Executive'];
export const STAFF_ROLES = ['Accounting', 'ProjectManager', 'Executive'];
export const ROLE_LABELS = {
  Worker: 'Field',
  ShopWorker: 'Shop',
  FieldSupervisor: 'Field Supervisor',
  Accounting: 'Office',
  ProjectManager: 'Project Manager',
  Executive: 'Executive',
};
